// NewRepoDialog —— 新建仓库对话框(对齐 docs/ui_dialog 规范)
// 支持两种模式:
//   - 单个:传统单仓库表单
//   - 批量:多行输入(每行一个仓库,格式"仓库名 [描述]"),逐个创建,显示进度
// 由 DashboardPage 内联使用;创建成功后回调 onCreated(关闭并打开仓库详情)。
import { useState, useMemo, type FormEvent } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Input,
  Label,
  Textarea,
  Radio,
  RadioGroup,
  Spinner,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  DialogActions,
  MessageBar,
  MessageBarBody,
  TabList,
  Tab,
  ProgressBar,
} from '@fluentui/react-components';
import {
  LockClosedRegular,
  GlobeRegular,
  AddRegular,
  CheckmarkRegular,
  DismissRegular,
  ListRegular,
} from '@fluentui/react-icons';
import { reposApi } from '@/api/repos';
import type { PublicRepo } from '@/api/types';
import { useAuthStore } from '@/store/auth';
import { explainError } from '@/api/client';
import { HelpPopover } from '@/components/HelpPopover';
import { DialogCloseTitle } from '@/components/DialogCloseTitle';
import { toast } from '@/lib/toast';

const NAME_RE = /^[a-z0-9._-]+$/;

const useStyles = makeStyles({
  field: {
    display: 'flex',
    flexDirection: 'column',
    marginBottom: tokens.spacingVerticalL,
    gap: tokens.spacingVerticalXS,
  },
  fieldError: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase300,
  },
  descriptionRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground3,
  },
  modeTabs: {
    marginBottom: tokens.spacingVerticalM,
  },
  batchTextarea: {
    fontFamily: tokens.fontFamilyMonospace,
    minHeight: '160px',
    resize: 'vertical',
  },
  batchHint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    lineHeight: 1.5,
  },
  batchProgress: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    marginBottom: tokens.spacingVerticalM,
  },
  batchStats: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  successCount: {
    color: tokens.colorStatusSuccessForeground1,
  },
  failCount: {
    color: tokens.colorStatusDangerForeground1,
  },
  failList: {
    maxHeight: '120px',
    overflowY: 'auto',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorStatusDangerForeground1,
    backgroundColor: tokens.colorStatusDangerBackground1,
    padding: tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusMedium,
    fontFamily: tokens.fontFamilyMonospace,
  },
});

type FieldErrors = {
  name?: string;
  description?: string;
};

type Mode = 'single' | 'batch';

interface BatchItem {
  name: string;
  description: string;
}

interface BatchResult {
  name: string;
  success: boolean;
  error?: string;
  repo?: PublicRepo;
}

export function NewRepoDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (repo: PublicRepo) => void;
}) {
  const styles = useStyles();
  const user = useAuthStore((s) => s.user);

  const [mode, setMode] = useState<Mode>('single');
  // 单个模式状态
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState<'public' | 'private'>('private');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<FieldErrors>({});

  // 批量模式状态
  const [batchText, setBatchText] = useState('');
  const [batchResults, setBatchResults] = useState<BatchResult[]>([]);
  const [batchProgress, setBatchProgress] = useState(0);
  const [batchSubmitting, setBatchSubmitting] = useState(false);

  // 解析批量输入:每行一个,格式 "仓库名 描述"(描述可选,空格分隔)
  const parsedBatch = useMemo<BatchItem[]>(() => {
    const lines = batchText.split('\n').map((l) => l.trim()).filter(Boolean);
    return lines.map((line) => {
      // 第一个空白之前是名字,剩下都是描述
      const m = line.match(/^(\S+)\s*(.*)$/);
      if (!m) return { name: line, description: '' };
      return { name: m[1], description: m[2].trim() };
    });
  }, [batchText]);

  function validateName(n: string): string | null {
    if (!n.trim()) return '请输入仓库名';
    if (n.length < 3 || n.length > 39) return '3-39 字符';
    if (!NAME_RE.test(n)) return '只允许小写字母、数字、_、-、.';
    if (/^[-.]/.test(n)) return '不能以 - 或 . 开头';
    return null;
  }

  function reset() {
    setName('');
    setDescription('');
    setVisibility('private');
    setError(null);
    setErrors({});
    setBatchText('');
    setBatchResults([]);
    setBatchProgress(0);
  }

  // ===== 单个模式 =====
  function validate() {
    const e: FieldErrors = {};
    const nameErr = validateName(name);
    if (nameErr) e.name = nameErr;
    if (description.length > 500) {
      e.description = '最多 500 字符';
    }
    setErrors(e);
    return !e.name && !e.description;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setError(null);
    setSubmitting(true);
    try {
      const repo = await reposApi.create({
        name: name.trim(),
        description: description || undefined,
        visibility,
      });
      toast.success(`仓库 ${repo.name} 创建成功`);
      onCreated(repo);
    } catch (err) {
      const msg = explainError(err);
      console.error(msg);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  // ===== 批量模式 =====
  async function handleBatchSubmit() {
    if (parsedBatch.length === 0) {
      setError('请至少输入一个仓库名');
      return;
    }
    // 先全量校验
    const invalid = parsedBatch
      .map((item, i) => ({ item, i, err: validateName(item.name) }))
      .filter((x) => x.err);
    if (invalid.length > 0) {
      const msg = invalid
        .slice(0, 5)
        .map((x) => `第 ${x.i + 1} 行「${x.item.name}」: ${x.err}`)
        .join('\n');
      setError(`有 ${invalid.length} 个仓库名校验不通过:\n${msg}${invalid.length > 5 ? '\n…' : ''}`);
      return;
    }
    // 描述长度校验
    const descInvalid = parsedBatch.filter((i) => i.description.length > 500);
    if (descInvalid.length > 0) {
      setError(`${descInvalid.length} 个仓库描述超过 500 字`);
      return;
    }

    setError(null);
    setBatchSubmitting(true);
    setBatchResults([]);
    setBatchProgress(0);

    const results: BatchResult[] = [];
    let successCount = 0;

    // 串行创建,避免并发打服务端
    for (let i = 0; i < parsedBatch.length; i++) {
      const item = parsedBatch[i];
      try {
        const repo = await reposApi.create({
          name: item.name,
          description: item.description || undefined,
          visibility,
        });
        results.push({ name: item.name, success: true, repo });
        successCount++;
      } catch (err) {
        results.push({
          name: item.name,
          success: false,
          error: explainError(err),
        });
      }
      setBatchProgress(i + 1);
      setBatchResults([...results]);
    }

    const failCount = results.length - successCount;
    if (successCount > 0) {
      toast.success(`成功创建 ${successCount} 个仓库`);
    }
    if (failCount > 0) {
      toast.error(`${failCount} 个仓库创建失败`);
    }
    // 全部成功 → 关闭对话框并刷新列表
    if (successCount > 0 && failCount === 0) {
      // 用第一个仓库回调 onCreated(触发列表刷新 + 打开详情)
      const first = results.find((r) => r.success)!;
      onCreated(first.repo!);
    }
    setBatchSubmitting(false);
  }

  return (
    <Dialog
      modalType="alert"
      open={open}
      onOpenChange={(_, d) => {
        if (!d.open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogSurface style={{ width: 600, maxWidth: '92vw' }}>
        <DialogBody>
          <DialogCloseTitle title="新建仓库" onClose={() => { reset(); onClose(); }} />
          <DialogContent>
            {error && (
              <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalL }}>
                <MessageBarBody style={{ whiteSpace: 'pre-wrap' }}>{error}</MessageBarBody>
              </MessageBar>
            )}

            {/* 模式切换 */}
            <TabList
              selectedValue={mode}
              onTabSelect={(_, d) => {
                setMode(d.value as Mode);
                setError(null);
              }}
              className={styles.modeTabs}
              size="small"
            >
              <Tab value="single">
                <AddRegular /> 单个
              </Tab>
              <Tab value="batch">
                <ListRegular /> 批量
              </Tab>
            </TabList>

            {/* 单个模式 */}
            {mode === 'single' && (
              <form onSubmit={handleSubmit} autoComplete="off">
                <div className={styles.field}>
                  <Label htmlFor="repo-name" required>
                    仓库名 <HelpPopover content="仓库的唯一路径标识(位于 owner 之下)。3–39 位,仅限小写字母、数字、下划线(_)、连字符(-)和点(.),且不能以 - 或 . 开头。" />
                  </Label>
                  <Input
                    id="repo-name"
                    value={name}
                    onChange={(_, d) => setName(d.value)}
                    contentBefore={
                      user?.username ? (
                        <Text size={300}>{user.username}/</Text>
                      ) : undefined
                    }
                    placeholder="my-awesome-project"
                    autoFocus
                  />
                  {errors.name && <span className={styles.fieldError}>{errors.name}</span>}
                </div>

                <div className={styles.field}>
                  <Label htmlFor="repo-description">
                    描述(可选) <HelpPopover content="一句话说明仓库的用途,最多 500 字,可留空。" />
                  </Label>
                  <Textarea
                    id="repo-description"
                    value={description}
                    onChange={(_, d) => setDescription(d.value)}
                    rows={3}
                    maxLength={500}
                    placeholder="一句话说明这个仓库是干什么的"
                  />
                  <div className={styles.descriptionRow}>{description.length}/500</div>
                  {errors.description && (
                    <span className={styles.fieldError}>{errors.description}</span>
                  )}
                </div>

                <div className={styles.field}>
                  <Label required>
                    可见性 <HelpPopover content="私有:仅你与协作者可见;公开:任何登录用户都能看到并克隆(取决于其权限)。" />
                  </Label>
                  <RadioGroup
                    value={visibility}
                    onChange={(_, d) => setVisibility(d.value as 'public' | 'private')}
                    layout="horizontal"
                  >
                    <Radio
                      value="private"
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <LockClosedRegular /> Private
                        </span>
                      }
                    />
                    <Radio
                      value="public"
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <GlobeRegular /> Public
                        </span>
                      }
                    />
                  </RadioGroup>
                </div>
              </form>
            )}

            {/* 批量模式 */}
            {mode === 'batch' && (
              <div>
                <div className={styles.field}>
                  <Label required>
                    仓库列表{' '}
                    <HelpPopover content="每行一个仓库,格式:仓库名 描述(描述可留空,用空格分隔)。例如:&#10;my-frontend 前端项目&#10;my-backend&#10;docs-api 文档服务" />
                  </Label>
                  <Textarea
                    value={batchText}
                    onChange={(_, d) => setBatchText(d.value)}
                    rows={8}
                    placeholder={
                      'my-frontend 前端项目\nmy-backend 后端 API\ndocs-service 文档服务\nconfig-center'
                    }
                    className={styles.batchTextarea}
                    autoFocus
                  />
                  <div className={styles.batchHint}>
                    每行一个仓库,格式: <code>仓库名 描述</code>(描述可选)。共 {parsedBatch.length} 个。
                  </div>
                </div>

                {/* 进度 + 结果 */}
                {(batchSubmitting || batchResults.length > 0) && (
                  <div className={styles.batchProgress}>
                    <ProgressBar
                      value={parsedBatch.length > 0 ? (batchProgress / parsedBatch.length) * 100 : 0}
                      max={100}
                    />
                    <div className={styles.batchStats}>
                      <span>进度: {batchProgress} / {parsedBatch.length}</span>
                      <span className={styles.successCount}>
                        <CheckmarkRegular /> 成功 {batchResults.filter((r) => r.success).length}
                      </span>
                      <span className={styles.failCount}>
                        <DismissRegular /> 失败 {batchResults.filter((r) => !r.success).length}
                      </span>
                    </div>
                    {batchResults.filter((r) => !r.success).length > 0 && (
                      <div className={styles.failList}>
                        {batchResults
                          .filter((r) => !r.success)
                          .map((r, i) => (
                            <div key={i}>
                              {r.name}: {r.error}
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                )}

                <div className={styles.field}>
                  <Label required>
                    可见性(全部) <HelpPopover content="批量创建的所有仓库使用同一个可见性设置。" />
                  </Label>
                  <RadioGroup
                    value={visibility}
                    onChange={(_, d) => setVisibility(d.value as 'public' | 'private')}
                    layout="horizontal"
                  >
                    <Radio
                      value="private"
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <LockClosedRegular /> Private
                        </span>
                      }
                    />
                    <Radio
                      value="public"
                      label={
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <GlobeRegular /> Public
                        </span>
                      }
                    />
                  </RadioGroup>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogActions>
            {mode === 'single' ? (
              <Button
                appearance="primary"
                disabled={submitting}
                icon={submitting ? <Spinner size="tiny" appearance="inverted" /> : undefined}
                onClick={handleSubmit as any}
              >
                创建
              </Button>
            ) : (
              <Button
                appearance="primary"
                disabled={batchSubmitting || parsedBatch.length === 0}
                icon={batchSubmitting ? <Spinner size="tiny" appearance="inverted" /> : <ListRegular />}
                onClick={handleBatchSubmit}
              >
                {batchSubmitting ? `创建中 ${batchProgress}/${parsedBatch.length}` : `批量创建 (${parsedBatch.length})`}
              </Button>
            )}
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
